import { HTMLAttributes, useState } from "react";
import { CommonInstallflowFrame } from "./common-install-flow-frame";
import previousTutorialImage from "@/assets/previous-tutorial.png";
import { useNavigate } from "@tanstack/react-router";
import { Clock } from "lucide-react";
import { apiClient } from "@/api/client";
import ThaloIcon from "@/assets/thalo_icon.svg";
import {
  installFlowOrganizationId,
  installFlowLocationId,
  installFlowSublocationId,
  installFlowSensorKitSerial,
  installFlowScanId,
  installFlowInstallationId,
} from "@/installFlow/storage-keys";

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export function PreviousTutorial({ className }: AuthProps) {
  const navigate = useNavigate();
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleNext = () => {
    navigate({ to: "/installFlow/physical-install-steps" });
  };

  const handleSkip = async () => {
    setIsLinking(true);
    setLinkError(null);
    try {
      const organizationId = installFlowOrganizationId.get();
      const locationId = installFlowLocationId.get();
      const sublocationId = installFlowSublocationId.get();
      const serialNumber = installFlowSensorKitSerial.get() ?? "";
      const scanId = installFlowScanId.get() ?? "";
      const installationId = installFlowInstallationId.get() ?? "";

      await apiClient.post(
        `organizations/${organizationId}/locations/${locationId}/sublocations/${sublocationId}/installations/${installationId}/link-sensor-kit/`,
        { scan_id: scanId, sensor_kit_serial_number: serialNumber },
      );

      await navigate({ to: "/installFlow/complete-physical-install" });
    } catch {
      setLinkError("Failed to link the sensor. Please try again.");
      setIsLinking(false);
    }
    
  };

  if (isLinking) {
    return (
      <div className="w-full max-w-[460px] min-h-screen flex flex-col items-center justify-center gap-6 p-6 bg-white">
        <div className="relative flex items-center justify-center">
          <div className="absolute w-32 h-32 rounded-full bg-blue-100 animate-ping opacity-30" />
          <div className="absolute w-24 h-24 rounded-full bg-blue-200 animate-ping opacity-40 [animation-delay:150ms]" />
          <img src={ThaloIcon} alt="Thalo" className="relative w-16 h-16 animate-bounce z-10" />
        </div>
        <p className="text-lg font-bold text-black">Linking sensor kit...</p>
        <p className="text-sm text-gray-500 text-center">This may take a few seconds.</p>
      </div>
    );
  }

  return (
    <CommonInstallflowFrame className="flex-col min-h-[calc(100vh-64px)]">
      {/* Scrollable content */}
      <div className="flex flex-col gap-4 w-full">
        {/* Image */}
        <div className="flex justify-center mb-4">
          <img
            src={previousTutorialImage}
            alt="Connect sensor to asset"
            className="w-64 h-64 object-contain"
          />
        </div>

        {/* Text content */}
        <h1 className="text-2xl font-bold text-black leading-tight">
          Finally, let's connect your sensor to the asset.
        </h1>
        <p className="text-base text-gray-500 leading-snug">
          Have basic hand tools and clear access to the unit ready. You may skip this tutorial if you're familiar with the install process.
        </p>

        {/* Time badge */}
        <div className="inline-flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-1.5 w-fit">
          <Clock className="w-4 h-4 text-[#0053EE]" />
          <span className="text-sm font-medium text-[#0053EE]">15 mins</span>
        </div>
      </div>

      {/* Bottom buttons */}
      <div className="flex  gap-3 w-full pt-8 pb-2 justify-between">
      <button
          type="button"
          onClick={handleSkip}
          className="w-full rounded-full bg-gray-100 py-4 text-gray-900 text-base font-semibold hover:bg-gray-200 transition-colors"
        >
          Skip tutorial
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="w-full rounded-full bg-[#0053EE] py-4 text-white text-base font-semibold hover:bg-blue-700 transition-colors"
        >
          Next
        </button>
       
      </div>
    </CommonInstallflowFrame>
  );
}
